// Purchase Report — master summary print / PDF / Excel export helpers

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

const COMPANY_FALLBACK = 'Lucky Broast';

const fmtDate = (v) => {
    const d = dayjs(v);
    return d.isValid() ? d.format('DD-MM-YYYY') : (v ?? '');
};

// Column definitions keyed by tab — matches PurchaseReport.js tab columns
export const REPORT_COLUMNS = {
    po: [
        { key: 'poID', label: 'PO No' },
        { key: 'entDate', label: 'Post Date', type: 'date' },
        { key: 'dispName', label: 'Created By' },
        { key: 'PartyName', label: 'Supplier' },
        { key: 'remarks', label: 'Remarks' },
        { key: 'deliveryStatus', label: 'Status' },
        { key: 'totalItems', label: 'Total Items', type: 'number' },
    ],
    grn: [
        { key: 'pdID', label: 'GRN No' },
        { key: 'entDate', label: 'Post Date', type: 'date' },
        { key: 'dispName', label: 'Created By' },
        { key: 'PartyName', label: 'Supplier' },
        { key: 'grnSourceLabel', label: 'GRN Source' },
        { key: 'remarks', label: 'Remarks' },
        { key: 'pdStatus', label: 'Status' },
        { key: 'totalItems', label: 'Total Items', type: 'number' },
    ],
    pi: [
        { key: 'piID', label: 'Invoice No' },
        { key: 'entDate', label: 'Post Date', type: 'date' },
        { key: 'dispName', label: 'Created By' },
        { key: 'PartyName', label: 'Supplier' },
        { key: 'remarks', label: 'Remarks' },
        { key: 'piStatus', label: 'Status' },
        { key: 'totalItems', label: 'Items', type: 'number' },
    ],
    issuance: [
        { key: 'issuanceID', label: 'ID' },
        { key: 'recordedAt', label: 'Date', type: 'date' },
        { key: 'issuedBy', label: 'Issued By' },
        { key: 'branchName', label: 'Branch' },
        { key: 'departmentName', label: 'Department' },
        { key: 'itemCode', label: 'Item Code' },
        { key: 'itemDesc', label: 'Item' },
        { key: 'quantity', label: 'Qty', type: 'number' },
        { key: 'inUnit', label: 'Unit' },
        { key: 'remarks', label: 'Remarks' },
    ],
};

const cellValue = (row, col) => {
    const raw = row[col.key];
    if (col.type === 'date') return fmtDate(raw);
    if (col.type === 'number') return Number(raw || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return raw ?? '';
};

// Detail column definitions per tab (item-level rows under each master)
export const DETAIL_COLUMNS = {
    po: [
        { key: 'itemCode', label: 'Item Code' },
        { key: 'itemDesc', label: 'Item' },
        { key: 'itemRate', label: 'Rate', type: 'number' },
        { key: 'quantity', label: 'Qty', type: 'number' },
        { key: 'amount', label: 'Amount', type: 'number' },
    ],
    grn: [
        { key: 'itemCode', label: 'Item Code' },
        { key: 'itemDesc', label: 'Item' },
        { key: 'itemRate', label: 'Rate', type: 'number' },
        { key: 'quantity', label: 'Qty', type: 'number' },
        { key: 'delivered', label: 'Delivered', type: 'number' },
        { key: 'amount', label: 'Amount', type: 'number' },
    ],
    pi: [
        { key: 'itemCode', label: 'Item Code' },
        { key: 'itemDesc', label: 'Item' },
        { key: 'itemRate', label: 'Rate', type: 'number' },
        { key: 'quantity', label: 'Qty', type: 'number' },
        { key: 'amount', label: 'Amount', type: 'number' },
    ],
    issuance: [
        { key: 'itemCode', label: 'Item Code' },
        { key: 'itemDesc', label: 'Item' },
        { key: 'quantity', label: 'Qty', type: 'number' },
        { key: 'inUnit', label: 'Unit' },
        { key: 'rate', label: 'Rate', type: 'number' },
        { key: 'amount', label: 'Amount', type: 'number' },
    ],
};

const buildFilterMeta = (filters = {}) => {
    const parts = [];
    if (filters.fromDate) parts.push(`From: ${filters.fromDate}`);
    if (filters.toDate) parts.push(`To: ${filters.toDate}`);
    if (filters.itemLabel) parts.push(`Item: ${filters.itemLabel}`);
    if (filters.supplierLabel) parts.push(`Supplier: ${filters.supplierLabel}`);
    if (filters.departmentLabel) parts.push(`Departments: ${filters.departmentLabel}`);    return parts.join('   |   ') || 'No filters';
};

// ---------- PDF ----------
// groups: [{ master: {...masterRow}, details: [...detailRows] }]
export function exportPurchaseReportPDF({ title, fileName, tabKey, groups, filters, user }) {
    const cols = REPORT_COLUMNS[tabKey];
    const dCols = DETAIL_COLUMNS[tabKey];
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const company = user?.cmpName || user?.company?.cmpName || COMPANY_FALLBACK;

    doc.setFontSize(14);
    doc.text(company, 14, 14);
    doc.setFontSize(11);
    doc.text(title, 14, 21);
    doc.setFontSize(8);
    doc.text(doc.splitTextToSize(`Applied Filters: ${buildFilterMeta(filters)}`, 265), 14, 27);
    doc.text(`Masters: ${groups.length}   Generated: ${dayjs().format('DD-MM-YYYY HH:mm')}`, 14, 35);

    autoTable(doc, {
        startY: 38,
        head: [cols.map((c) => c.label)],
        body: groups.flatMap((g) => {
            const masterRow = cols.map((c) => ({
                content: cellValue(g.master, c),
                styles: { fontStyle: 'bold', fillColor: [240, 240, 240] },
            }));
            const detailHeader = dCols.map((c) => ({
                content: c.label,
                styles: { fontStyle: 'italic', textColor: [90, 90, 90], fillColor: [250, 250, 250] },
            }));
            const detailRows = g.details.map((d) =>
                dCols.map((c) => ({ content: cellValue(d, c), styles: { textColor: [70, 70, 70] } }))
            );
            // Per-master total row
            const totalQty = g.details.reduce((s, d) => s + Number(d.quantity || 0), 0);
            const totalAmt = g.details.reduce((s, d) => s + Number(d.amount || 0), 0);
            const totalRow = dCols.map((c) => ({
                content: c.key === 'amount' ? totalAmt : c.key === 'quantity' ? totalQty : (c.key === 'itemDesc' ? 'Total' : ''),
                styles: { fontStyle: 'bold', fillColor: [255, 248, 223] },
            }));
            return [masterRow, detailHeader, ...detailRows, totalRow];
        }),
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [120, 2, 2] },
        theme: 'grid',
    });

    if (filters?.grandTotals) {
        const totals = filters.departmentTotals || [];
        autoTable(doc, {
            startY: (doc.lastAutoTable?.finalY || 34) + 5,
            head: [['Department-wise Grand Totals', 'Documents', 'Quantity', 'Amount']],
            body: [...totals.map((department) => [department.departmentName, department.documents, Number(department.quantity || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), Number(department.amount || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })]), ['Overall Grand Total', filters.grandTotals.documents, Number(filters.grandTotals.quantity || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), Number(filters.grandTotals.amount || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })]],
            styles: { fontSize: 8, cellPadding: 1.5 },
            headStyles: { fillColor: [120, 2, 2] },
            didParseCell: (data) => { if (data.section === 'body' && data.row.index === totals.length) data.cell.styles.fontStyle = 'bold'; },
            theme: 'grid',
        });
    }
    doc.save(`${fileName || title.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD_HHmm')}.pdf`);
}

// ---------- Excel ----------
export function exportPurchaseReportExcel({ title, fileName, tabKey, groups, filters = {} }) {
    const cols = REPORT_COLUMNS[tabKey];
    const dCols = DETAIL_COLUMNS[tabKey];
    const data = [
        { 'Report Information': title },
        { 'Report Information': 'Applied Filters', Details: buildFilterMeta(filters) },
        { 'Report Information': 'Generated', Details: dayjs().format('DD-MM-YYYY HH:mm') },
        { 'Report Information': 'Documents', Details: groups.length },
        {}
    ];

    groups.forEach((g) => {
        // Master row
        const masterRow = {};
        cols.forEach((c) => { masterRow[c.label] = cellValue(g.master, c); });
        data.push(masterRow);
        // Detail header row
        const detailHeadRow = {};
        cols.forEach((c, i) => { detailHeadRow[c.label] = ''; });
        dCols.forEach((dc, di) => {
            if (di < cols.length) detailHeadRow[cols[di].label] = dc.label;
        });
        data.push(detailHeadRow);
        // Detail rows
        g.details.forEach((d) => {
            const detailRow = {};
            cols.forEach((c) => { detailRow[c.label] = ''; });
            dCols.forEach((dc, di) => {
                if (di < cols.length) detailRow[cols[di].label] = cellValue(d, dc);
            });
            data.push(detailRow);
        });
        // Per-master total row
        const totalQty = g.details.reduce((s, d) => s + Number(d.quantity || 0), 0);
        const totalAmt = g.details.reduce((s, d) => s + Number(d.amount || 0), 0);
        const totalRow = {};
        cols.forEach((c) => { totalRow[c.label] = ''; });
        const qtyIdx = dCols.findIndex((c) => c.key === 'quantity');
        const amtIdx = dCols.findIndex((c) => c.key === 'amount');
        const descIdx = dCols.findIndex((c) => c.key === 'itemDesc');
        if (descIdx >= 0 && descIdx < cols.length) totalRow[cols[descIdx].label] = 'Total';
        if (qtyIdx >= 0 && qtyIdx < cols.length) totalRow[cols[qtyIdx].label] = totalQty.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        if (amtIdx >= 0 && amtIdx < cols.length) totalRow[cols[amtIdx].label] = totalAmt.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        data.push(totalRow);
    });

    if (filters.grandTotals) {
        const { departmentTotals = [], grandTotals } = filters;
        data.push({});
        data.push({ 'Department-wise Grand Totals': 'Department', Documents: 'Documents', Quantity: 'Quantity', Amount: 'Amount' });
        departmentTotals.forEach((department) => data.push({
            'Department-wise Grand Totals': department.departmentName,
            Documents: department.documents,
            Quantity: Number(department.quantity || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            Amount: Number(department.amount || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        }));
        data.push({
            'Department-wise Grand Totals': 'Overall Grand Total',
            Documents: grandTotals.documents,
            Quantity: Number(grandTotals.quantity || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            Amount: Number(grandTotals.amount || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        });
    }
    const sheet = XLSX.utils.json_to_sheet(data);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, title.slice(0, 28));
    XLSX.writeFile(book, `${fileName || title.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
}

// ---------- Print (HTML window) ----------
const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');

export function printPurchaseReport({ title, tabKey, groups, filters, user }) {
    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!printWindow) return alert('Please allow pop-ups to print the report.');

    const cols = REPORT_COLUMNS[tabKey];
    const dCols = DETAIL_COLUMNS[tabKey];
    const company = user?.cmpName || user?.company?.cmpName || COMPANY_FALLBACK;
    const head = cols.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('');

    const body = groups.map((g) => {
        const masterRow = `<tr class="master">${cols.map((c) => `<td class="${c.type === 'number' ? 'num' : ''}">${escapeHtml(cellValue(g.master, c))}</td>`).join('')}</tr>`;
        const detailHead = `<tr class="dhead">${dCols.map((c) => `<td>${escapeHtml(c.label)}</td>`).join('')}${'<td></td>'.repeat(Math.max(0, cols.length - dCols.length))}</tr>`;
        const detailRows = g.details.map((d) =>
            `<tr class="detail">${dCols.map((c) => `<td class="${c.type === 'number' ? 'num' : ''}">${escapeHtml(cellValue(d, c))}</td>`).join('')}${'<td></td>'.repeat(Math.max(0, cols.length - dCols.length))}</tr>`
        ).join('');
        // Per-master total row
        const totalQty = g.details.reduce((s, d) => s + Number(d.quantity || 0), 0);
        const totalAmt = g.details.reduce((s, d) => s + Number(d.amount || 0), 0);
        const totalCells = dCols.map((c) => {
            const val = c.key === 'amount' ? totalAmt.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : c.key === 'quantity' ? totalQty.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (c.key === 'itemDesc' ? 'Total' : '');
            return `<td class="${c.type === 'number' ? 'num' : ''}">${escapeHtml(val)}</td>`;
        }).join('');
        const totalRow = `<tr class="total">${totalCells}${'<td></td>'.repeat(Math.max(0, cols.length - dCols.length))}</tr>`;
        return masterRow + detailHead + detailRows + totalRow;
    }).join('');

    const summaryHtml = filters?.grandTotals ? `<section class="summary"><h3>Department-wise Grand Totals</h3><table><thead><tr><th>Department</th><th>Documents</th><th>Quantity</th><th>Amount</th></tr></thead><tbody>${(filters.departmentTotals || []).map((department) => `<tr><td>${escapeHtml(department.departmentName)}</td><td class="num">${escapeHtml(department.documents)}</td><td class="num">${escapeHtml(Number(department.quantity || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))}</td><td class="num">${escapeHtml(Number(department.amount || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))}</td></tr>`).join('')}<tr class="grand"><td>Overall Grand Total</td><td class="num">${escapeHtml(filters.grandTotals.documents)}</td><td class="num">${escapeHtml(Number(filters.grandTotals.quantity || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))}</td><td class="num">${escapeHtml(Number(filters.grandTotals.amount || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))}</td></tr></tbody></table></section>` : '';
    printWindow.document.write(`<!doctype html><html><head><title>${escapeHtml(title)}</title><style>
@page{size:A4 landscape;margin:8mm}body{font:9px Arial,sans-serif;color:#20242b}
h1{font-size:15px;margin:0}h2{font-size:12px;margin:4px 0;font-weight:600}
.meta{font-size:9px;color:#444;margin-bottom:6px}
table{width:100%;border-collapse:collapse}th{background:#780202;color:#fff;padding:4px;text-align:left;white-space:nowrap}
td{padding:3px 4px;border-bottom:1px solid #ddd}.num{text-align:right}
tr.master td{font-weight:bold;background:#f0f0f0}
tr.dhead td{font-style:italic;color:#555;background:#fafafa;font-size:8px;border-bottom:1px solid #ccc}
tr.detail td{color:#444;padding-left:10px;font-size:8.5px}
tr.total td{font-weight:bold;background:#fff8df;border-top:1px solid #999;font-size:8.5px}.summary{margin-top:14px;width:55%}.summary h3{font-size:10px;margin:0 0 4px}.summary .grand td{font-weight:bold;background:#fff8df}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>
<h1>${escapeHtml(company)}</h1><h2>${escapeHtml(title)}</h2>
<div class="meta">${escapeHtml(buildFilterMeta(filters))} &nbsp;|&nbsp; Masters: ${groups.length} &nbsp;|&nbsp; Generated: ${escapeHtml(dayjs().format('DD-MM-YYYY HH:mm'))} &nbsp;|&nbsp; By: ${escapeHtml(user?.dispName || '-')}</div>
<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>${summaryHtml}
<script>window.onload=()=>{window.focus();window.print();};</script>
</body></html>`);
    printWindow.document.close();
}
